import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Button, Space, Tag, Alert, Skeleton, Input, Select, Pagination } from 'antd';
import { useNavigate } from 'react-router-dom';
import { EyeOutlined, SearchOutlined, FireOutlined, AppstoreOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import auctionService from '../services/auctionService';
import { toggleFavoriteAuction } from '../redux/userSlice';
import AuctionCard from '../components/Auction/AuctionCard';
import AuctionCardSkeleton from '../components/Auction/AuctionCardSkeleton';
import AuctionCountdown from '../components/Common/AuctionCountdown';
import AnimatedEmojis from '../components/Common/AnimatedEmojis';
import moment from 'moment';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const LiveAuctions = () => {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [meta, setMeta] = useState({ total_live: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 12,
        total: 0
    });
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);

    useEffect(() => {
        fetchLiveAuctions();
    }, [searchQuery, sortBy, sortDirection, pagination.current, pagination.pageSize]);

    // Sync local state with Redux favorites
    useEffect(() => {
        if (currentUser) {
            // Ensure favorite_auctions is initialized
            const favoriteAuctions = currentUser.favorite_auctions || [];
            setAuctions(prevAuctions => 
                prevAuctions.map(auction => ({
                    ...auction,
                    is_favorited: favoriteAuctions.includes(auction.id)
                }))
            );
        }
    }, [currentUser?.favorite_auctions]);

    const fetchLiveAuctions = async () => {
        try {
            setLoading(true);
            const params = {
                search: searchQuery,
                sort_by: sortBy,
                sort_direction: sortDirection,
                page: pagination.current,
                per_page: pagination.pageSize
            };
            const response = await auctionService.getLiveAuctions(params);
            
            // Process auctions with favorites
            const favoriteAuctions = currentUser?.favorite_auctions || [];
            const auctionsWithFavorites = response.data.map(auction => ({
                ...auction,
                is_favorited: favoriteAuctions.includes(auction.id)
            }));
            
            setAuctions(auctionsWithFavorites);
            setMeta(response.meta);
            
            // Update pagination from response meta
            if (response.meta) {
                setPagination(prev => ({
                    ...prev,
                    current: response.meta.current_page || pagination.current,
                    total: response.meta.total || 0,
                    pageSize: response.meta.per_page || prev.pageSize
                }));
            }
            
            setError(null);
        } catch (error) {
            console.error('Error fetching live auctions:', error);
            setError('Failed to load live auctions. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleFavoriteToggle = (auctionId) => {
        // Optimistically update local state
        setAuctions(prevAuctions => 
            prevAuctions.map(auction => 
                auction.id === auctionId 
                    ? { ...auction, is_favorited: !auction.is_favorited }
                    : auction
            )
        );

        // Optimistically update Redux state
        dispatch(toggleFavoriteAuction(auctionId));
    };

    // Removed handleCountdownComplete - no longer refreshing on countdown complete
    // const handleCountdownComplete = (auction) => {
    //     // Refresh the auction data when countdown completes
    //     fetchLiveAuctions();
    // };

    const handlePaginationChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize
        }));
    };

    const handleSearch = () => {
        setSearchQuery(searchInput);
        // Reset to first page when searching
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const renderSkeleton = () => (
        <div>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Row gutter={[24, 24]}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((item) => (
                        <Col xs={24} sm={12} md={8} key={item}>
                            <AuctionCardSkeleton />
                        </Col>
                    ))}
                </Row>
            </Space>
        </div>
    );

    return (
        <>
        <div className="live-auctions">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
           

                {/* Search and Sort Controls - Always Visible */}
                <div className="live-auctions-header" style={{ 
                    marginBottom: 40,
                    padding: '40px',
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    boxShadow: '0 20px 60px rgba(24, 144, 255, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    margin: '0 0 40px 0'
                }}>
                    {/* Enhanced background decoration */}
                    <div style={{
                        position: 'absolute',
                        top: '-50%',
                        right: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'radial-gradient(circle, rgba(6, 26, 123, 0.25) 0%, transparent 70%)',
                        animation: 'float 6s ease-in-out infinite'
                    }} />
                    <div style={{
                        position: 'absolute',
                        bottom: '-30%',
                        left: '-30%',
                        width: '160%',
                        height: '160%',
                        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
                        animation: 'float 8s ease-in-out infinite reverse'
                    }} />
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 100,
                        height: 100,
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '50%',
                        zIndex: 1,
                        animation: 'pulse 4s ease-in-out infinite'
                    }} />

                    <div style={{ position: 'relative', zIndex: 2 }}>
                        <div className="live-auctions-header-content" style={{ marginBottom: '24px' }}>
                            <Title level={1} className="live-auctions-title" style={{ 
                                margin: 0, 
                                color: 'white',
                                fontWeight: 800,
                                fontSize: '42px',
                                lineHeight: 1.2,
                                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                                Live Auctions
                            </Title>
                            <Text className="live-auctions-subtitle" style={{ 
                                fontSize: '20px',
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontWeight: 400,
                                display: 'block',
                                marginTop: '12px',
                                lineHeight: 1.5
                            }}>
                                Discover and bid on exciting live auctions in real-time
                            </Text>
                            
                            {/* Animated Emojis Line */}
                            <div className="live-auctions-emojis-container">
                                <AnimatedEmojis 
                                    emojis={['🐂', '🐃', '🐏', '🐑', '🐐', '🐎', '🫏', '🐔', '🐓', '🐣', '🦆', '🦃', '🦙']}
                                    spacing={120}
                                    speed={90}
                                />
                            </div>
                            
                            <div className="live-auctions-tag-container" style={{ marginTop: 24 }}>
                                <Tag 
                                    color="white" 
                                    className="live-auctions-tag"
                                    style={{ 
                                        color: '#1890ff',
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        padding: '8px 20px',
                                        borderRadius: '25px',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    {loading ? 'Loading auctions...' : `${meta.total_live} Active Auctions`}
                                </Tag>
                            </div>
                        </div>

                        <div className="live-auctions-search-container" style={{ 
                            background: 'rgba(255, 255, 255, 0.98)',
                            borderRadius: '16px',
                            padding: '32px',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}>
                            <Row gutter={[20, 20]} align="middle">
                                <Col xs={24} sm={24} md={16}>
                                    <div className="search-container">
                                        <Input
                                            placeholder="Search auctions by title, description, or category..."
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                            onPressEnter={handleSearch}
                                            allowClear
                                            size="large"
                                            className="search-input"
                                        />
                                        <Button
                                            type="primary"
                                            icon={<SearchOutlined />}
                                            onClick={handleSearch}
                                            className="search-button"
                                            size="large"
                                        >
                                            <span className="search-button-text">Search</span>
                                        </Button>
                                    </div>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <div style={{ position: 'relative' }}>
                        <Select
                                            placeholder="Sort by"
                            value={`${sortBy}-${sortDirection}`}
                            onChange={(value) => {
                                const [newSortBy, newSortDirection] = value.split('-');
                                setSortBy(newSortBy);
                                setSortDirection(newSortDirection);
                            }}
                            size="large"
                                            style={{
                                                width: '100%',
                                                height: '56px',
                                                fontSize: '16px',
                                                backgroundColor: 'transparent',
                                                lineHeight: '56px',
                                            }}
                                            dropdownStyle={{
                                                borderRadius: '12px',
                                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                                border: '1px solid #e8eaed'
                                            }}
                                            dropdownMatchSelectWidth={false}
                        >
                            <Option value="created_at-desc">Recent Live</Option>
                            <Option value="created_at-asc">Oldest First</Option>
                            <Option value="title-asc">Title A-Z</Option>
                            <Option value="title-desc">Title Z-A</Option>
                            <Option value="start_time-desc">Latest Start</Option>
                            <Option value="start_time-asc">Earliest Start</Option>
                            <Option value="lots_count-desc">Most Lots</Option>
                            <Option value="lots_count-asc">Fewest Lots</Option>
                        </Select>
                                    </div>
                                </Col>
                            </Row>
                       
                        </div>
                    </div>
                </div>

                {loading ? (
                    renderSkeleton()
                ) : error ? (
                    <Alert
                        message="Error"
                        description={error}
                        type="error"
                        showIcon
                    />
                ) : auctions.length === 0 ? (
                    <Alert
                        message="No Live Auctions"
                        description="There are currently no live auctions. Please check back later."
                        type="info"
                        showIcon
                    />
                ) : (
                    <>
                        <Row gutter={[24, 24]}>
                            {auctions.map((auction) => (
                                <Col xs={24} sm={12} md={8} key={auction.id}>
                                    <div style={{ position: 'relative' }}>
                                        <AuctionCard
                                            auction={auction}
                                            isLiveAuction={true}
                                            onFavoriteToggle={handleFavoriteToggle}
                                        />
                                        {/* Real-time countdown overlay */}
                                        <AuctionCountdown
                                            auction={auction}
                                            variant="overlay"
                                        />
                                    </div>
                                </Col>
                            ))}
                        </Row>
                        
                        {/* Pagination */}
                        {pagination.total > 0 && (
                            <div style={{ 
                                marginTop: 32,
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '24px 0'
                            }}>
                                <Pagination
                                    current={pagination.current}
                                    pageSize={pagination.pageSize}
                                    total={pagination.total}
                                    onChange={handlePaginationChange}
                                    onShowSizeChange={handlePaginationChange}
                                    // showSizeChanger
                                    // showQuickJumper
                                    // showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} auctions`}
                                    // pageSizeOptions={['10', '20', '50', '100']}
                                />
                            </div>
                        )}
                    </>
                )}
            </Space>
        </div>
            
            <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-20px) rotate(180deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
                    50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.1); }
                }

                /* Search Container Styles */
                .search-container {
                    display: flex;
                    align-items: center;
                    width: 100%;
                }

                .search-input {
                    flex: 1;
                    border-radius: 12px 0 0 12px !important;
                    border: 2px solid #e8eaed !important;
                    border-right: none !important;
                    font-size: 16px !important;
                    height: 56px !important;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04) !important;
                }

                .search-input:focus,
                .search-input:hover {
                    border-color: #1890ff !important;
                    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1) !important;
                }

                .search-button {
                    height: 56px !important;
                    border-radius: 0 12px 12px 0 !important;
                    background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%) !important;
                    border: 2px solid #e8eaed !important;
                    border-left: none !important;
                    font-weight: 600 !important;
                    font-size: 16px !important;
                    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3) !important;
                    padding: 0 28px !important;
                    transition: all 0.3s ease !important;
                }

                .search-button:hover {
                    background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%) !important;
                    transform: translateY(-1px) !important;
                    box-shadow: 0 6px 16px rgba(24, 144, 255, 0.4) !important;
                }

                .search-button-text {
                    display: inline;
                }

                /* Mobile Responsive Styles */
                @media (max-width: 768px) {
                    .live-auctions-header {
                        padding: 20px 16px !important;
                        margin: 0 0 24px 0 !important;
                    }
                    
                    .live-auctions-title {
                        font-size: 28px !important;
                        line-height: 1.3 !important;
                    }
                    
                    .live-auctions-subtitle {
                        font-size: 18px !important;
                        margin-top: 8px !important;
                    }
                    
                    .live-auctions-search-container {
                        padding: 20px 16px !important;
                        border-radius: 12px !important;
                    }
                    
                    .search-container {
                        flex-direction: row;
                        gap: 0;
                    }
                    
                    .search-input {
                        border-radius: 12px 0 0 12px !important;
                        border-right: none !important;
                        width: calc(100% - 60px) !important;
                        height: 56px !important;
                        font-size: 16px !important;
                        padding: 0 16px !important;
                    }
                    
                    .search-button {
                        border-radius: 0 12px 12px 0 !important;
                        border-left: none !important;
                        width: 60px !important;
                        height: 56px !important;
                        padding: 0 !important;
                        min-width: 60px !important;
                    }
                    
                    .search-button-text {
                        display: none !important;
                    }
                    
                    .search-button .anticon {
                        font-size: 18px !important;
                    }
                    
                    .live-auctions-search-container .ant-row {
                        margin: 0 !important;
                    }
                    
                    .live-auctions-search-container .ant-col {
                        padding: 0 !important;
                    }
                    
                    .live-auctions-search-container .ant-select {
                        height: 56px !important;
                        font-size: 14px !important;
                    }
                    
                    .live-auctions-tag-container {
                        margin-top: 16px !important;
                    }
                    
                    .live-auctions-tag {
                        font-size: 14px !important;
                        padding: 6px 16px !important;
                    }
                    
                    .live-auctions-emojis-container {
                        margin-top: 12px !important;
                    }
                    
                    .live-auctions-header-content {
                        margin-bottom: 16px !important;
                    }
                }

                @media (max-width: 480px) {
                    .live-auctions-header {
                        padding: 16px 12px !important;
                        margin: 0 0 20px 0 !important;
                    }
                    
                    .live-auctions-title {
                        font-size: 24px !important;
                        line-height: 1.3 !important;
                    }
                    
                    .live-auctions-subtitle {
                        font-size: 20px !important;
                        margin-top: 6px !important;
                    }
                    
                    .live-auctions-search-container {
                        padding: 16px 12px !important;
                        border-radius: 10px !important;
                    }
                    
                    .search-input {
                        height: 50px !important;
                        font-size: 14px !important;
                        padding: 0 12px !important;
                    }
                    
                    .search-button {
                        height: 50px !important;
                        width: 56px !important;
                        min-width: 56px !important;
                    }
                    
                    .search-input {
                        width: calc(100% - 56px) !important;
                    }
                    
                    .search-button .anticon {
                        font-size: 16px !important;
                    }
                    
                    .live-auctions-search-container .ant-select {
                        height: 50px !important;
                        font-size: 13px !important;
                    }
                    
                    .live-auctions-tag-container {
                        margin-top: 12px !important;
                    }
                    
                    .live-auctions-tag {
                        font-size: 12px !important;
                        padding: 5px 12px !important;
                    }
                    
                    .live-auctions-emojis-container {
                        margin-top: 8px !important;
                    }
                    
                    .live-auctions-header-content {
                        margin-bottom: 12px !important;
                    }
                }

                /* Live Auctions Card Hover Animations */
                .live-auctions .ant-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
                }

                .live-auctions .ant-card:hover .hover-overlay {
                    opacity: 1;
                }

                /* Ensure the card container has the proper class for hover effects */
                .live-auctions {
                    padding: 0 0;
                    background: #ffffff;
                    min-height: 100vh;
                }
            `}</style>
        </>
    );
};

export default LiveAuctions; 